import { CardTypes, currencyBinMap, countryCurrency } from './ccgdata'

// Currently Indian domestic payments (INR) requires 3DS for both Visa and Mastercard
// This may move to remote config in the future if more countries are required in the future.
const need3DsCountries = ['IN']

/**
 * Returns a random int between a range (inclusive)
 * Poached from https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/random
 * @param {int} maxRange The upper range int
 * @param {int} minRange The lower range int
 * @returns {int} Random int
 */
const getRandomInt = (maxRange, minRange) => {
  const min = Math.ceil(minRange || 0)
  const max = Math.floor(maxRange)

  return Math.floor(Math.random() * (max - min + 1)) + min // The maximum is inclusive and the minimum is inclusive
}

/**
 * Calculates the last digit of a credit card number as per Luhn algorithm
 * @param {string} cardNumber The card number except for the last digit
 * @param {int} digitCount The total number of digits for the card type
 * @returns {int} The last digit for the card
 */
const getLastDigit = (cardNumber, digitCount) => {
  // Reverse card number to calculate the final digit
  const reversedCCnumber = cardNumber.split('').reverse().map(Number)

  // Calculatations for the final digit
  let sum = 0
  let pos = 0
  let odd
  let checkDigit

  while (pos < digitCount - 1) {
    odd = reversedCCnumber[pos] * 2
    if (odd > 9) {
      odd -= 9
    }

    sum += odd

    if (pos !== digitCount - 2) {
      sum += reversedCCnumber[pos + 1]
    }

    pos += 2
  }

  // calculate and assign final digit
  checkDigit = ((Math.floor(sum / 10) + 1) * 10 - sum) % 10

  return checkDigit
}

/**
 * @function getExpiryDate
 * @param {boolean} is3ds If the expiry date needs to be 3ds compliant for testing purposes, as per Cardinal
 * @returns {object} Contains the expiry month and expiry year
 */
const getExpiryDate = (is3ds) => {
  let expiryMonth
  let expiryYear

  const currYear = new Date().getFullYear()

  if (is3ds) {
    /* If the card needs to be used for 3ds payments, we will assign the first month of the following year (eg: 01/2021),
    as this appears to be the logic that Cardinal currently uses. This may potentially change. */
    expiryMonth = 1
    expiryYear = currYear + 1
  } else {
    expiryMonth = getRandomInt(12, 1)
    expiryYear = currYear + getRandomInt(5, 1)
  }

  return {
    expiryMonth,
    expiryYear,
  }
}

/**
 * @function generateCardNumber
 * @param {string} cardType credit card type
 * @param {string} prefix bin prefix - for card number
 * @param {boolean} is3ds If the expiry date needs to be 3ds compliant for testing purposes, as per Cardinal
 * @returns {object} credit card object
 */
const generateCardNumber = (cardType, prefix, is3ds) => {
  let digitCount
  let cvvCount
  let cvv2
  let cardNumber = ''
  let checkDigit

  // add prefix to start the card number
  cardNumber += prefix

  // check appropriate digit and cvv counts
  digitCount = CardTypes[cardType].digitCount
  cvvCount = CardTypes[cardType].cvvCount

  // Assign expiry date
  let { expiryMonth, expiryYear } = getExpiryDate(is3ds)

  // Pad the month with a zero, if needed
  if (expiryMonth < 10) {
    expiryMonth = '0' + expiryMonth
  }

  // Assign random cvv2
  if (cvvCount === 3) {
    cvv2 = '' + getRandomInt(999)
  } else if (cvvCount === 4) {
    cvv2 = '' + getRandomInt(9999)
  }

  // Pad the cvv2 with zeros, if needed
  while (cvv2.length < cvvCount) {
    cvv2 = '0' + cvv2
  }

  // Assign random card numbers except for the last one
  while (cardNumber.length < digitCount - 1) {
    cardNumber += getRandomInt(9)
  }

  // Calculate and get the last digit
  checkDigit = getLastDigit(cardNumber, digitCount)
  cardNumber += checkDigit

  return {
    cardNumber,
    expiryMonth,
    expiryYear,
    cvv2,
  }
}

/**
 * @function creditCardGeneratorPost
 * @param {object} req request object
 * @param {object} res response object
 * @param {function} next the callback to signify the handler is complete
 */
export const creditCardGenerator = (cardType, countryCode) => {
  let responseMessage
  let errorObj
  let prefixList
  let selectedPrefix
  let currencySelected
  let is3ds = false

  const supportedType = cardType?.toLowerCase()

  if (typeof CardTypes[supportedType] !== 'undefined') {
    // check hardcoded list for 3-D Secure requirement
    if (need3DsCountries.indexOf(countryCode) >= 0) {
      is3ds = true
    }

    // given countryCode, retrieve currency for that country using countryCurrency JSON
    currencySelected = countryCurrency[countryCode] || 'USD'

    // if currencyBinMap received back from Remote Config, parse and get currency for selected country
    prefixList =
      currencyBinMap[
        currencySelected + '_TYPE_CARD_' + cardType?.toUpperCase()
      ] || ''

    if (prefixList === '') {
      // if remote config fails or an entry is not found use default bin prefixes from hardcoded CardTypes JSON
      prefixList = CardTypes[supportedType].prefixList.toString()
    }

    prefixList = prefixList.split(',')
    // if multiple prefixes found, pick one at random, otherwise select single prefix as final

    if (prefixList.length > 1) {
      selectedPrefix = prefixList[getRandomInt(prefixList.length - 1)]
    } else {
      selectedPrefix = prefixList[0]
    }

    const { cardNumber, expiryMonth, expiryYear, cvv2 } = generateCardNumber(
      supportedType,
      selectedPrefix,
      is3ds,
    )

    responseMessage = {
      card_type: cardType,
      card_number: cardNumber,
      expiry_month: expiryMonth,
      expiry_year: expiryYear,
      cvv2,
    }

    return responseMessage
  }

  // Card type not supported. Return error.
  errorObj = {
    message: 'The credit card type specified is not supported.',
  }

  return errorObj
}

// Add luhnCheck and genCC for testing and export default
const luhnCheck = (cardNumber) => {
  if (!/^\d+$/.test(cardNumber)) return false
  let sum = 0
  let shouldDouble = false
  for (let i = cardNumber.length - 1; i >= 0; i--) {
    let digit = parseInt(cardNumber[i], 10)
    if (shouldDouble) {
      digit *= 2
      if (digit > 9) digit -= 9
    }
    sum += digit
    shouldDouble = !shouldDouble
  }
  return sum % 10 === 0
}

const genCC = (prefix, length) => {
  if (prefix.length >= length) throw new Error('Prefix too long')
  let number = prefix
  while (number.length < length - 1) {
    number += Math.floor(Math.random() * 10)
  }
  // Calculate Luhn check digit
  let sum = 0
  let shouldDouble = true
  for (let i = number.length - 1; i >= 0; i--) {
    let digit = parseInt(number[i], 10)
    if (shouldDouble) {
      digit *= 2
      if (digit > 9) digit -= 9
    }
    sum += digit
    shouldDouble = !shouldDouble
  }
  const checkDigit = (10 - (sum % 10)) % 10
  return number + checkDigit
}

export default {
  generateCardNumber,
  creditCardGenerator,
  luhnCheck,
  genCC,
}
